import {useCallback} from 'react'
import type {ReactNode} from 'react'
import {ActionMenu} from '@primer/react-brand/esm'

import {getAnalyticsEvent} from '../../lib/utils/analytics'

type ActionMenuLinkItemProps = {
  href: string
  analyticsEvent: ReturnType<typeof getAnalyticsEvent>
  children: ReactNode
  openInNewTab?: boolean
  handler?: (newValue: string) => void
  type?: 'none' | 'single' | 'link'
}

/**
 * `ActionMenu.Item` applies `target` and `rel` to its wrapping list item
 * instead of the inner anchor. Handle new-tab navigation from the bubbling
 * click while still forwarding Primer's handler for close/focus restoration.
 */
export function ActionMenuLinkItem({
  href,
  analyticsEvent,
  children,
  openInNewTab,
  handler,
  type,
}: ActionMenuLinkItemProps) {
  const analyticsData = analyticsEvent['data-analytics-event']
  const itemRef = useCallback(
    (item: HTMLLIElement | null) => {
      if (!item) return

      item.removeAttribute('tabindex')
      item.setAttribute('role', 'none')

      if (item.firstElementChild instanceof HTMLAnchorElement) {
        const anchor = item.firstElementChild

        anchor.setAttribute('role', 'menuitem')
        if (analyticsData) {
          anchor.setAttribute('data-analytics-event', analyticsData)
          item.removeAttribute('data-analytics-event')
        }
      }
    },
    [analyticsData],
  )
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLLIElement>) => {
      if (openInNewTab) {
        event.preventDefault()
        window.open(href, '_blank', 'noopener,noreferrer')
      }
      handler?.(href)
    },
    [handler, href, openInNewTab],
  )

  return (
    <ActionMenu.Item ref={itemRef} as="a" href={href} type={type} onClick={handleClick} {...analyticsEvent}>
      {children}
    </ActionMenu.Item>
  )
}
